import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useState } from 'preact/hooks';
import { X, Building2, Save } from 'lucide-preact';
import { clientCompanyApi } from '@/api/clientCompany';
import { CompanyEditForm } from './CompanyEditForm';
import { handleApiError } from '../../../utils/errorHandling';
import { useToast } from '../../../hooks/useToast';
export function CompanyEditModal({ isOpen, company, onClose, onSaved }) {
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(false);
    const { showError, showSuccess } = useToast();
    useEffect(() => {
        if (isOpen) {
            setEditing({
                ETag: company.ETag,
                AuthOrganizationId: company.AuthOrganizationId,
                Name: company.Name,
                CompanyId: company.CompanyId,
                CompanyUrl: company.CompanyUrl,
                CompanyType: company.CompanyType,
                Address: company.Address,
                Status: company.Status
            });
        }
    }, [isOpen, company]);
    if (!isOpen)
        return null;
    // Derive a fallback form state from the incoming company while local state initializes
    const formCompany = editing || {
        ETag: company.ETag,
        AuthOrganizationId: company.AuthOrganizationId,
        Name: company.Name,
        CompanyId: company.CompanyId,
        CompanyUrl: company.CompanyUrl,
        CompanyType: company.CompanyType,
        Address: company.Address,
        Status: company.Status
    };
    const handleSave = async (payload) => {
        setLoading(true);
        try {
            const body = payload || formCompany;
            const res = await clientCompanyApi.update(company.Id, body);
            if (res.error) {
                // Global handler shows toasts for 422; handleApiError handles others
                handleApiError(res, showError, () => { }, 'Failed to save company');
            }
            else if (res.data) {
                // Keep workspaces from original in case API doesn't include them
                const updated = { ...res.data, Workspaces: company.Workspaces };
                onSaved(updated);
                showSuccess('Success', 'Company information updated successfully');
                onClose();
            }
        }
        catch (err) {
            handleApiError(err, showError, () => { }, 'Failed to save company');
        }
        finally {
            setLoading(false);
        }
    };
    return (_jsx("div", { className: "fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4", children: _jsxs("div", { className: "bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col", children: [_jsxs("div", { className: "flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center", children: _jsx(Building2, { className: "w-5 h-5 text-primary-600 dark:text-primary-400" }) }), _jsxs("div", { children: [_jsx("h2", { className: "text-xl font-semibold text-slate-900 dark:text-white", children: "Edit Company" }), _jsx("p", { className: "text-sm text-slate-600 dark:text-slate-300", children: "Update company information" })] })] }), _jsx("button", { onClick: onClose, className: "p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors", children: _jsx(X, { className: "w-5 h-5" }) })] }), _jsx("div", { className: "flex-1 overflow-y-auto p-6", children: _jsx(CompanyEditForm, { company: formCompany, onSave: (vals) => handleSave(vals) }) }), _jsxs("div", { className: "px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-3", children: [_jsx("button", { onClick: onClose, className: "px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800", children: "Cancel" }), _jsxs("button", { form: "company-edit-form", type: "submit", disabled: loading, className: "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50", children: [_jsx(Save, { className: "w-4 h-4" }), loading ? 'Saving…' : 'Save Changes'] })] })] }) }));
}
